import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { User, CheckCircle, Check, XCircle, X, Clock, Shirt, ClipboardList, Trash2 } from "lucide-react";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { useParams, useSearchParams } from "react-router-dom";
import { getRsvpsApi, deleteRsvpApi } from "../../api/cardApi";
import { useAuth } from "../../Context/AuthContext";
import { format12Hour, formatTimeRange } from "../../utils/FormatTime";
import { Calendar, MapPin } from "lucide-react";

const RSVPStatusComponent = ({ cardId: cardIdProp, hideNavbar = true }) => {
  const { cardId: cardIdParam } = useParams();
  const cardId = cardIdProp ?? cardIdParam;
  const { cards } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvp] = useState([]);
  const [cardData, setCardData] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!hideNavbar) return;
    const navbar = document.querySelector(".main-navbar");

    if (navbar) navbar.style.display = "none";

    return () => {
      if (navbar) navbar.style.display = "";
    };
  }, [hideNavbar]);


  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!cardId) return;

    // We pass source=api via URL query (?source=api&slug=adam-hawa)
    // or infer it if cardId is short (uniqueCode) instead of long Firebase ID
    const explicitSource = searchParams.get("source");
    const isApi = explicitSource === 'api' || cardId.length < 10;

    const fetchFirebaseData = async () => {
      try {
        const cardRef = doc(db, "cards", cardId);
        const cardSnap = await getDoc(cardRef);

        if (!cardSnap.exists()) {
          console.error("Card not found");
          setLoading(false);
          return;
        }

        setCardData(cardSnap.data());

        const rsvpQuery = query(
          collection(db, "cards", cardId, "rsvp"),
          orderBy("createdAt", "desc")
        );

        const rsvpSnap = await getDocs(rsvpQuery);

        const rsvpData = rsvpSnap.docs.map((doc) => {
          const { slot: _slot, ...rest } = doc.data();
          return { id: doc.id, ...rest };
        });

        setRsvp(rsvpData);
        setLoading(false);
      } catch (error) {
        console.error("Error loading card:", error);
        setLoading(false);
      }
    };

    const fetchApiData = async () => {
      try {
        const rsvpData = await getRsvpsApi(cardId);
        // Normalize to look like firebase data
        const normalized = rsvpData.map((r) => ({
          id: r.id,
          name: r.guestName,
          attendant: r.attendanceStatus === "attending" ? "attend" : "notAttend",
          guestCount: r.pax,
          slot: r.slot || null,
          createdAt: r.createdAt ? { toDate: () => new Date(r.createdAt) } : null,
        }));
        setRsvp(normalized);
        console.log('normalized', normalized);
        // if API doesn't return totalGuest, we can just omit it or fallback
        setLoading(false);
      } catch (error) {
        console.error("Error loading API RSVP:", error);
        setLoading(false);
      }
    };

    if (isApi) {
      fetchApiData();
      // Optional: Polling to see updates without refresh (since user requested auto update)
      const intervalId = setInterval(fetchApiData, 15000);
      return () => clearInterval(intervalId);
    } else {
      fetchFirebaseData();
    }
  }, [cardId, searchParams]);

  if (loading) {
    return <Typography align="center">Loading card...</Typography>;
  }

  // Calculations
  const attendingPax = rsvps
    .filter((r) => r.attendant === "attend")
    .reduce((sum, r) => sum + (r.guestCount || 0), 0);

  const notAttendingCount = rsvps.filter(
    (r) => r.attendant === "notAttend"
  ).length;

  // Pull totalGuest from the global cards cache (fetched on login)
  const cachedCard = cards.find(c => c.uniqueCode === cardId);
  const totalGuest = cachedCard?.totalGuest ?? cardData?.totalGuest ?? null;

  const groomBrideName = cachedCard?.greetingName ?? cardData?.greetingName ?? null;
  const eventDate = cachedCard?.eventDate ?? cardData?.eventDate ?? null;
  const eventAddress = cachedCard?.eventAddress ?? cardData?.eventAddress ?? null;
  const eventType = cachedCard?.eventType ?? cardData?.eventType ?? null;
  const startTime = cachedCard?.startTime ?? cardData?.startTime ?? null;
  const endTime = cachedCard?.endTime ?? cardData?.endTime ?? null;
  const dressCode = cachedCard?.dressCode ?? cardData?.dressCode ?? null;
  const totalResponses = rsvps.length;

  // When slots are used, total pax = sum of each slot's totalGuest capacity
  const slotTimes = cachedCard?.slotTimes ?? cardData?.slotTimes ?? [];
  const slotTotalGuest = slotTimes.length > 0
    ? slotTimes.reduce((sum, s) => sum + (Number(s.totalGuest) || 0), 0)
    : null;

  // Show slot column only if at least one RSVP has slot data
  const hasSlot = rsvps.some((r) => r.slot && (r.slot.start || r.slot.label));
  // Responsive grid: tighter on mobile when slot column is present
  // Always include action (delete) column since this component is owner-only
  const gridCols = hasSlot
    ? { xs: "1.2fr 0.8fr 0.9fr 0.5fr 0.4fr", sm: "2fr 1.2fr 1.2fr 0.8fr 0.4fr" }
    : { xs: "1.4fr 0.9fr 0.6fr 0.4fr", sm: "2fr 1fr 1fr 0.4fr" };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    try {
      await deleteRsvpApi(cardId, deleteTargetId);
      setRsvp((prev) => prev.filter((r) => r.id !== deleteTargetId));
    } catch (err) {
      console.error("Failed to delete RSVP:", err?.response?.status, err?.response?.data);
    } finally {
      setDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        py: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Title */}
      <Typography sx={{ fontSize: 22 }}>
        RSVP Status
      </Typography>

      {/* Summary Cards */}
      <Box
        sx={{
          mt: 3,
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr 1fr", sm: "1fr 1fr 1fr" },
          gap: 2,
          width: "100%",
        }}
      >
        {/* Attend */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "18px",
            p: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            textAlign: "center",
            justifyItems: "center",
            alignContent: "center",
          }}
        >
          <span className='rsvp-stat-icon rsvp-icon-hadir-outline'>
            <Check size={20} strokeWidth={2.5} />
          </span>
          <Typography sx={{ fontSize: 13, mt: 1 }}>Hadir</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: "bold" }}>
            {attendingPax}
          </Typography>
        </Box>

        {/* Not Attend */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "18px",
            p: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            textAlign: "center",
            justifyItems: "center",
            alignContent: "center",
          }}
        >
          <span className='rsvp-stat-icon rsvp-icon-tidak-outline'>
            <X size={20} strokeWidth={2.5} />
          </span>
          <Typography sx={{ fontSize: 13, mt: 1 }}>Tidak Hadir</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: "bold" }}>
            {notAttendingCount}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "18px",
            p: 2,
            width: "100%",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            textAlign: "center",
            alignContent: "center",
            justifyItems: "center",
          }}
        >
          <span className='rsvp-stat-icon rsvp-icon-pax-outline' >
            <User color="#2494a3" size={20} />
          </span>
          <Typography sx={{ fontSize: 13, mt: 1 }}>Majlis Pax</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: "bold" }}>
            {hasSlot ? (slotTotalGuest ?? "-") : (totalGuest ?? "-")}
          </Typography>
        </Box>
      </Box>

      {/* Wedding Info Card */}
      <Box
        sx={{
          mt: 3,
          width: "100%",
          borderRadius: "20px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          p: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Centered Header Section */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 1 }}>
          {eventType && (
            <Box
              sx={{
                backgroundColor: "rgba(36, 148, 163, 0.15)",
                color: "#2494a3",
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.5px",
                px: 2,
                py: 1,
                borderRadius: "20px",
                textAlign: "center",
                display: "inline-block",
              }}
            >
              {eventType}
            </Box>
          )}
          {groomBrideName && (
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#333",
                textAlign: "center",
                mt: 1,
                mb: 1
              }}
            >
              {groomBrideName}
            </Typography>
          )}
        </Box>

        {/* Divider */}
        {(eventDate || startTime || endTime || eventAddress) && (
          <Box sx={{ width: "100%", height: "1px", backgroundColor: "rgba(59, 59, 59, 0.08)" }} />
        )}

        {/* Vertically Stacked Info Items */}
        {eventDate && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
            <Calendar size={18} color="#2494a3" style={{ marginTop: 2, opacity: 0.9, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
                {cardData?.language === "EN" || cachedCard?.language === "EN" ? "Date" : "Tarikh"}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "rgba(59, 59, 59, 0.7)", mt: 0.5 }}>
                {new Date(eventDate).toLocaleDateString(
                  cardData?.language === "EN" || cachedCard?.language === "EN" ? "en-US" : "ms-MY",
                  {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }
                )}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Divider */}
        {eventDate && (startTime || endTime) && (
          <Box sx={{ width: "100%", height: "1px", backgroundColor: "rgba(59, 59, 59, 0.08)" }} />
        )}

        {(startTime || endTime) && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
            <Clock size={18} color="#2494a3" style={{ marginTop: 2, opacity: 0.9, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
                {cardData?.language === "EN" || cachedCard?.language === "EN" ? "Time" : "Masa"}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "rgba(59, 59, 59, 0.7)", mt: 0.5 }}>
                {formatTimeRange(startTime, endTime)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Divider */}
        {(startTime || endTime) && eventAddress && (
          <Box sx={{ width: "100%", height: "1px", backgroundColor: "rgba(59, 59, 59, 0.08)" }} />
        )}

        {eventAddress && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
            <MapPin size={18} color="#2494a3" style={{ marginTop: 2, opacity: 0.9, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
                {cardData?.language === "EN" || cachedCard?.language === "EN" ? "Location" : "Lokasi"}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "rgba(59, 59, 59, 0.7)", mt: 0.5, whiteSpace: "pre-line" }}>
                {eventAddress}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>


      <Box
        sx={{
          mt: 2,
          width: "100%",
        }}
      >
        <Typography sx={{ fontSize: 15, mb: 2, mt: 2 }}>
          Senarai Responder
        </Typography>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: gridCols,
              px: { xs: 1.5, sm: 3 },
              py: 1.2,
              backgroundColor: "#F3F3F3",
              color: "#4F4F4F",
            }}
          >
            <Typography sx={{ fontSize: { xs: 11, sm: 13 } }}> Nama</Typography>
            <Typography sx={{ fontSize: { xs: 11, sm: 13 } }} align="center">Status</Typography>
            {hasSlot && (
              <Typography sx={{ fontSize: { xs: 11, sm: 13 } }} align="center">Slot Waktu</Typography>
            )}
            <Typography sx={{ fontSize: { xs: 11, sm: 13 } }} align="center">Pax</Typography>
          </Box>

          {/* Table Body */}
          <Box
            sx={{
              maxHeight: 350,
              overflowY: "scroll",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {rsvps.map((rsvp, index) => (
              <Box key={rsvp.id}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: gridCols,
                    alignItems: "center",
                    px: { xs: 1.5, sm: 3 },
                    py: 1.2,
                    borderBottom: "1px solid rgba(0,0,0,0.06)"
                  }}
                >
                  {/* Name */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: { xs: 11, sm: 13 } }}>
                      {rsvp.name || "Tetamu"}
                    </Typography>
                  </Box>

                  {/* Status */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Box
                      sx={{
                        py: 0.5,
                        borderRadius: "999px",
                        fontSize: { xs: 10, sm: 11 },
                        letterSpacing: "0.2px",
                        backgroundColor:
                          rsvp.attendant === "attend"
                            ? "rgba(46, 125, 50, 0.12)"
                            : "rgba(198, 40, 40, 0.12)",
                        color:
                          rsvp.attendant === "attend"
                            ? "#2e7d32"
                            : "#c62828",
                        minWidth: { xs: 60, sm: 80 },
                        textAlign: "center",
                        px: { xs: 0.5, sm: 1 },
                      }}
                    >
                      {rsvp.attendant === "attend" ? "Hadir" : "Tidak Hadir"}
                    </Box>
                  </Box>

                  {/* Slot Waktu */}
                  {hasSlot && (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      {rsvp.slot ? (
                        <Box
                          sx={{
                            py: 0.4,
                            px: { xs: 0.5, sm: 1 },
                            fontSize: { xs: 10, sm: 11 },
                            textAlign: "center",
                            lineHeight: 1.4,
                          }}
                        >
                          {rsvp.slot.label && (
                            <div style={{ fontWeight: 600 }}>{rsvp.slot.label}</div>
                          )}
                          {rsvp.slot.start && rsvp.slot.end && (
                            <div>{format12Hour(rsvp.slot.start)} – {format12Hour(rsvp.slot.end)}</div>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: { xs: 11, sm: 13 } }} align="center">-</Typography>
                      )}
                    </Box>
                  )}

                  {/* Guest Count */}
                  <Typography align="center" sx={{ fontSize: { xs: 11, sm: 13 } }}>
                    {rsvp.attendant === "attend"
                      ? rsvp.guestCount || 1
                      : "-"}
                  </Typography>

                  {/* Delete Action */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTargetId(rsvp.id)}
                      sx={{
                        color: "#c62828",
                        opacity: 0.7,
                        "&:hover": { opacity: 1, backgroundColor: "rgba(198,40,40,0.08)" },
                        p: 0.5,
                      }}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </Box>
                </Box>

                {/* Divider */}
                {index !== rsvps.length - 1 && (
                  <Divider sx={{ opacity: 0.05 }} />
                )}
              </Box>
            ))}

            {rsvps.length === 0 && (
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: 13,
                  py: 3,
                  opacity: 0.6,
                }}
              >
                Tiada data RSVP
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTargetId}
        onClose={() => !deleteLoading && setDeleteTargetId(null)}
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Padam RSVP</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Adakah anda pasti ingin memadam rekod RSVP ini? Rekod yang telah dipadam tidak boleh dikembalikan.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteTargetId(null)}
            disabled={deleteLoading}
            sx={{ textTransform: "none", color: "#4F4F4F", fontSize: 13, backgroundColor: "#e7e7e7ff", borderRadius: "50px" }}
          >
            Batal
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            sx={{
              textTransform: "none",
              fontSize: 13,
              color: "#c62828",
              backgroundColor: "#fbbcbca8",
              borderRadius: "50px",
              minWidth: 90,
            }}
          >
            {deleteLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Padam"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>

  );
};

export default RSVPStatusComponent;
